import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';
import { RelatedQuestionUpdatePayload, RelatedQuestionItem } from './types';
import { createGeneratedQuestion } from './utils';

// Extended payload type to include content type
interface ExtendedRelatedQuestionUpdatePayload extends RelatedQuestionUpdatePayload {
  contentType?: 'research' | 'private_research';
}

export const useRelatedQuestionUpdate = () => {
  const queryClient = useQueryClient();

  const updateRelatedQuestionMutation = useMutation({
    mutationFn: async (payload: ExtendedRelatedQuestionUpdatePayload) => {
      console.log('[useRelatedQuestionUpdate] Updating related question:', payload);

      const contentType = payload.contentType || 'research'; // Default to public research for backward compatibility
      const tableName = contentType === 'private_research' ? 'workbook_research' : 'research_library';
      
      console.log('[useRelatedQuestionUpdate] Using table:', tableName, 'for content type:', contentType);

      // First, get the current research to access its related_questions
      const { data: currentResearch, error: fetchError } = await supabase
        .from(tableName)
        .select('related_questions')
        .eq('id', payload.researchId)
        .single();

      if (fetchError) {
        console.error('[useRelatedQuestionUpdate] Error fetching current research:', fetchError);
        throw new Error(`Failed to fetch current research: ${fetchError.message}`);
      }

      if (!currentResearch) {
        throw new Error('Research not found');
      }

      // Get the current related questions array - handle both old string[] and new mixed format
      let currentQuestions: any[] = [];
      if (Array.isArray(currentResearch.related_questions)) {
        currentQuestions = currentResearch.related_questions;
      } else if (currentResearch.related_questions) {
        // Handle case where it might be stored as jsonb but needs parsing
        try {
          currentQuestions = JSON.parse(JSON.stringify(currentResearch.related_questions));
        } catch (parseError) {
          console.warn('[useRelatedQuestionUpdate] Failed to parse related_questions, treating as empty array');
          currentQuestions = [];
        }
      }
      
      // Validate the question index
      if (payload.questionIndex < 0 || payload.questionIndex >= currentQuestions.length) {
        throw new Error(`Invalid question index: ${payload.questionIndex}. Array length: ${currentQuestions.length}`);
      }

      // Create the updated questions array
      const updatedQuestions = [...currentQuestions];
      
      // Replace the string question at the specified index with the generated question object
      updatedQuestions[payload.questionIndex] = createGeneratedQuestion(
        payload.originalQuestion,
        payload.generatedResearchId
      );

      console.log('[useRelatedQuestionUpdate] Updated questions array:', updatedQuestions);

      // Update the research in the appropriate database table
      const { data, error: updateError } = await supabase
        .from(tableName)
        .update({
          related_questions: updatedQuestions,
          updated_at: new Date().toISOString()
        })
        .eq('id', payload.researchId)
        .select()
        .single();

      if (updateError) {
        console.error('[useRelatedQuestionUpdate] Error updating research:', updateError);
        throw new Error(`Failed to update research: ${updateError.message}`);
      }

      console.log('[useRelatedQuestionUpdate] Research updated successfully:', data);
      return data;
    },
    onSuccess: (data, payload) => {
      console.log('[useRelatedQuestionUpdate] Mutation succeeded, invalidating queries');
      
      const contentType = payload.contentType || 'research';
      
      if (contentType === 'private_research') {
        // CRITICAL FIX: Invalidate private research item details cache (IMMEDIATE UI UPDATE)
        // This is the key fix - the lightbox now uses this dedicated cache
        queryClient.invalidateQueries({
          queryKey: ['private-research-item-details', payload.researchId],
          exact: true,
          refetchType: 'all'  // Force immediate refetch for real-time UI updates
        });
        
        // Also invalidate the general item details cache pattern
        queryClient.invalidateQueries({
          queryKey: ['private-research-item-details'],
          exact: false,
          refetchType: 'all'
        });
        
        // 1. Invalidate private research generator queries (main page cache)
        queryClient.invalidateQueries({
          queryKey: ['private-research-generator'],
          exact: false,
          refetchType: 'all'  // Force refetch of all cached variations
        });
        
        // 2. Invalidate private research count queries
        queryClient.invalidateQueries({
          queryKey: ['private-research-generator-count'],
          exact: false,
          refetchType: 'all'
        });
        
        // 3. Invalidate universal storage queries
        queryClient.invalidateQueries({
          queryKey: ['universal-storage-items'],
          exact: false,
          refetchType: 'all'
        });
        
        queryClient.invalidateQueries({
          queryKey: ['universal-library-data'],
          exact: false,
          refetchType: 'all'
        });
        
        // 4. Invalidate library counts (for sidebar counters)
        queryClient.invalidateQueries({
          queryKey: ['universal-library-counts'],
          exact: false,
          refetchType: 'all'
        });
      } else {
        // Invalidate public research related queries (existing logic)
        queryClient.invalidateQueries({
          queryKey: ['research-details', payload.researchId]
        });
        
        queryClient.invalidateQueries({
          queryKey: ['research-item-details', payload.researchId]
        });
        
        queryClient.invalidateQueries({
          queryKey: ['research-library']
        });

        queryClient.invalidateQueries({
          queryKey: ['research-library-list'],
          exact: false
        });
      }

      toast({
        title: 'Question generated',
        description: 'The related question has been converted to research',
      });
    },
    onError: (error: any) => {
      console.error('[useRelatedQuestionUpdate] Mutation failed:', error);
      toast({
        title: 'Update failed',
        description: error.message || 'Failed to update the related question',
        variant: 'destructive',
      });
    }
  });

  return {
    updateRelatedQuestion: updateRelatedQuestionMutation.mutate,
    isUpdating: updateRelatedQuestionMutation.isPending,
    error: updateRelatedQuestionMutation.error
  };
};

